import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

export interface CartItem {
  id: number;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  size?: string;
  color?: string;
}

interface CartContextValue {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (id: number, size?: string, color?: string) => void;
  updateItemQuantity: (
    id: number,
    size: string | undefined,
    color: string | undefined,
    quantity: number
  ) => void;
  clearCart: () => void;
  total: number;
  count: number;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

const STORAGE_KEY = 'cart_items';

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const { user, token } = useAuth();

  useEffect(() => {
    const load = async () => {
      if (user && token) {
        try {
          const res = await fetch(`${API_BASE_URL}/me/cart`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (res.ok) {
            const data = await res.json();
            const serverItems = Array.isArray(data) ? data : data.items;
            if (Array.isArray(serverItems)) {
              const normalized: CartItem[] = serverItems.map((i: any) => ({
                id: Number(i.id ?? i.product_id),
                name: i.name,
                price: Number(i.price),
                quantity: Number(i.quantity),
                size: i.size || undefined,
                color: i.color || undefined,
                image: i.image || undefined,
              }));
              setItems(normalized);
            } else {
              setItems([]);
            }
          } else {
            setItems([]);
          }

          try {
            localStorage.removeItem(STORAGE_KEY);
          } catch (e) {
            console.error('Erreur lors de la suppression du panier local', e);
          }
        } catch (e) {
          console.error('Erreur lors du chargement du panier depuis le serveur', e);
          setItems([]);
        }
      } else {
        setItems([]);
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch (e) {
          console.error('Erreur lors de la suppression du panier local', e);
        }
      }
    };

    load();
  }, [user, token]);

  useEffect(() => {
    if (!(user && token)) {
      return;
    }

    (async () => {
      try {
        await fetch(`${API_BASE_URL}/me/cart`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ items }),
        });
      } catch (e) {
        console.error('Erreur lors de la sauvegarde du panier sur le serveur', e);
      }
    })();
  }, [items, user, token]);

  const addItem = (item: CartItem) => {
    setItems((prev) => {
      const existingIndex = prev.findIndex(
        (i) =>
          i.id === item.id &&
          i.size === item.size &&
          i.color === item.color
      );

      if (existingIndex !== -1) {
        const updated = [...prev];
        const existing = updated[existingIndex];
        updated[existingIndex] = {
          ...existing,
          quantity: existing.quantity + item.quantity,
        };
        return updated;
      }

      return [...prev, item];
    });
  };

  const removeItem = (id: number, size?: string, color?: string) => {
    setItems((prev) =>
      prev.filter((item) => {
        if (item.id !== id) {
          return true;
        }

        if (size !== undefined && item.size !== size) {
          return true;
        }

        if (color !== undefined && item.color !== color) {
          return true;
        }

        return false;
      })
    );
  };

  const updateItemQuantity = (
    id: number,
    size: string | undefined,
    color: string | undefined,
    quantity: number
  ) => {
    if (quantity <= 0) {
      setItems((prev) =>
        prev.filter(
          (item) =>
            !(
              item.id === id &&
              item.size === size &&
              item.color === color
            )
        )
      );
      return;
    }

    setItems((prev) =>
      prev.map((item) =>
        item.id === id && item.size === size && item.color === color
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const count = items.reduce((sum, item) => sum + item.quantity, 0);

  const value: CartContextValue = {
    items,
    addItem,
    removeItem,
    updateItemQuantity,
    clearCart,
    total,
    count,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart doit être utilisé dans un CartProvider');
  }
  return context;
};
